import './assets/styles/index.css'
import './i18n'
import './firebase'
import 'moment/locale/es'
import 'moment/locale/fr'
import 'moment/locale/it'
import 'moment/locale/en-gb'
import 'moment/locale/ca'
import '@fontsource/dm-sans'
import '@fontsource/dm-sans/500.css'
import '@fontsource/dm-sans/600.css'
import '@fontsource/dm-sans/700.css'

import { PostHogProvider } from '@posthog/react'
import * as Sentry from '@sentry/react'
import { SnackbarProvider } from 'notistack'
import posthog from 'posthog-js'
import React from 'react'
import ReactDOM from 'react-dom/client'
import {
  createRoutesFromChildren,
  matchRoutes,
  RouterProvider,
  useLocation,
  useNavigationType
} from 'react-router-dom'

import ErrorPage from './ErrorPage.tsx'
import { AppProviders } from './providers/AppProviders'
import { router } from './router'
import { initUserIdentification } from './utils/ProductAnalytics.ts'
import { getSessionId } from './utils/session'

const APP_VERSION = import.meta.env.VITE_APP_VERSION

const sessionId = getSessionId()

Sentry.init({
  dsn: 'https://95dd7dd924172d0c931ab744b2833fa8@o1354288.ingest.us.sentry.io/4507537687052288',
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes
    }),
    Sentry.replayIntegration(),
    Sentry.captureConsoleIntegration({
      levels: ['error']
    })
  ],
  release: `51trips-web@${APP_VERSION}`,
  beforeSend: (event) => {
    event.tags = {
      ...event.tags,
      sessionId: sessionId
    }
    return event
  },
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    'localhost',
    /^https:\/\/trips51-tripbook-eu-tll752sjda-ew\.a\.run\.app/
  ],
  // Session Replay.
  //
  // Replay records the DOM and then buffers every mutation it sees, which is
  // expensive wherever the page churns a lot of nodes — the tripbook editor's
  // virtualized page list being the worst case. Inside the iOS in-app web view
  // that buffering competes for the same tight memory budget as the pages'
  // decoded photos, and recording every session was contributing to WebKit
  // killing the web content process and reloading the editor mid-edit. (The
  // page list itself is also excluded from recording — see the
  // `data-sentry-block` on the scrollers in `TripBookEditorPage` and
  // `TripBookReviewScroller`.)
  //
  // So: sample a tenth of sessions for general behaviour, but keep every
  // session that actually errors, which is the one we always want to watch back.
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  environment: import.meta.env.VITE_ENV_NAME,
  enabled: import.meta.env.VITE_ENV_NAME !== 'dev'
})

// Initialize PostHog before user identification so the SDK is ready when
// Firebase auth resolves. Guarded behind env vars — the app boots fine
// without them, but dev builds warn loudly so the missing config is noticed.
const _posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN
const _posthogHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST
if (_posthogKey && _posthogHost) {
  posthog.init(_posthogKey, {
    api_host: _posthogHost,
    defaults: '2026-01-30'
  })
} else if (import.meta.env.DEV) {
  // eslint-disable-next-line no-console
  console.error(
    'VITE_PUBLIC_POSTHOG_PROJECT_TOKEN variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once VITE_PUBLIC_POSTHOG_PROJECT_TOKEN is configured'
  )
}

initUserIdentification()

// Routes are code-split. After a deploy, a tab still running the previous build
// asks for chunks that no longer exist; reload once to pick up the new build.
window.addEventListener('vite:preloadError', (event) => {
  const key = 'chunk-reload-at'
  const last = Number(sessionStorage.getItem(key) || 0)
  if (Date.now() - last < 10_000) return // already retried; let the error page show
  sessionStorage.setItem(key, String(Date.now()))
  event.preventDefault()
  window.location.reload()
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <PostHogProvider client={posthog}>
      <React.Suspense>
        <SnackbarProvider>
          <AppProviders>
            <Sentry.ErrorBoundary fallback={<ErrorPage />}>
              <RouterProvider router={router} />
            </Sentry.ErrorBoundary>
          </AppProviders>
        </SnackbarProvider>
      </React.Suspense>
    </PostHogProvider>
  </React.StrictMode>
)
